/** XeAgents.ai - Combined Plan & Topup Management with Tabbed View */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Plus, Loader2, Pencil, Trash2, DollarSign, Users, Phone, CreditCard, RefreshCw, Check, X, Coins, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  country: string;
  seatingEnabled: boolean;
  maxSeats: number;
  monthlyPrice: string;
  yearlyPrice: string | null;
  razorpayMonthlyPrice: string | null;
  razorpayYearlyPrice: string | null;
  maxAgents: number;
  maxCampaigns: number;
  maxContactsPerCampaign: number;
  maxWebhooks: number;
  maxKnowledgeBases: number;
  maxFlows: number;
  maxPhoneNumbers: number;
  phoneNumberPurchaseCredits: number;
  includedCredits: number;
  perMinuteRate: string | null;
  canChooseLlm: boolean;
  canPurchaseNumbers: boolean;
  useSystemPool: boolean;
  isActive: boolean;
  stripeProductId?: string | null;
  stripeMonthlyPriceId?: string | null;
  stripeYearlyPriceId?: string | null;
  razorpayPlanId?: string | null;
  razorpayYearlyPlanId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PlanFormData {
  name: string;
  displayName: string;
  description: string;
  country: string;
  seatingEnabled: boolean;
  maxSeats: number;
  monthlyPrice: number;
  yearlyPrice: number | null;
  razorpayMonthlyPrice: number | null;
  razorpayYearlyPrice: number | null;
  maxAgents: number;
  maxCampaigns: number;
  maxContactsPerCampaign: number;
  maxWebhooks: number;
  maxKnowledgeBases: number;
  maxFlows: number;
  maxPhoneNumbers: number;
  phoneNumberPurchaseCredits: number;
  includedCredits: number;
  perMinuteRate: number | null;
  canChooseLlm: boolean;
  canPurchaseNumbers: boolean;
  useSystemPool: boolean;
}

interface CreditPackage {
  id: string;
  name: string;
  description?: string;
  credits: number;
  price: number;
  razorpayPrice?: number | null;
  paypalPrice?: number | null;
  paystackPrice?: number | null;
  mercadopagoPrice?: number | null;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  razorpayItemId?: string | null;
  country?: string;
  isActive: boolean;
}

interface TopupFormData {
  name: string;
  description: string;
  credits: number;
  price: number;
  razorpayPrice: number;
  paypalPrice: number;
  paystackPrice: number;
  mercadopagoPrice: number;
  country: string;
}

interface PaymentGatewayConfig {
  stripeEnabled: boolean;
  razorpayEnabled: boolean;
  paypalEnabled: boolean;
  paystackEnabled: boolean;
  mercadopagoEnabled: boolean;
  stripeCurrency?: string;
  stripeCurrencySymbol?: string;
  paypalCurrency?: string;
  paypalCurrencySymbol?: string;
  paystackCurrencies?: string[];
  paystackDefaultCurrency?: string;
  mercadopagoCurrency?: string;
  mercadopagoCurrencySymbol?: string;
}

const defaultPlanFormData: PlanFormData = {
  name: "",
  displayName: "",
  description: "",
  country: "US",
  seatingEnabled: false,
  maxSeats: 1,
  monthlyPrice: 0,
  yearlyPrice: null,
  razorpayMonthlyPrice: null,
  razorpayYearlyPrice: null,
  maxAgents: 1,
  maxCampaigns: 1,
  maxContactsPerCampaign: 100,
  maxWebhooks: 3,
  maxKnowledgeBases: 5,
  maxFlows: 3,
  maxPhoneNumbers: 0,
  phoneNumberPurchaseCredits: 25,
  includedCredits: 0,
  perMinuteRate: null,
  canChooseLlm: false,
  canPurchaseNumbers: false,
  useSystemPool: false,
};

const defaultTopupFormData: TopupFormData = {
  name: "",
  description: "",
  credits: 100,
  price: 10,
  razorpayPrice: 0,
  paypalPrice: 0,
  paystackPrice: 0,
  mercadopagoPrice: 0,
  country: "US",
};

export default function PlanAndTopupManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("plans");
  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  // Plan state
  const [isPlanCreateDialogOpen, setIsPlanCreateDialogOpen] = useState(false);
  const [isPlanEditDialogOpen, setIsPlanEditDialogOpen] = useState(false);
  const [isPlanDeleteDialogOpen, setIsPlanDeleteDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [planFormData, setPlanFormData] = useState<PlanFormData>(defaultPlanFormData);

  // Topup state
  const [isTopupCreateDialogOpen, setIsTopupCreateDialogOpen] = useState(false);
  const [isTopupEditDialogOpen, setIsTopupEditDialogOpen] = useState(false);
  const [isTopupDeleteDialogOpen, setIsTopupDeleteDialogOpen] = useState(false);
  const [editingTopup, setEditingTopup] = useState<CreditPackage | null>(null);
  const [deletingTopup, setDeletingTopup] = useState<CreditPackage | null>(null);
  const [topupFormData, setTopupFormData] = useState<TopupFormData>(defaultTopupFormData);

  // Fetch plans
  const { data: plans, isLoading: isLoadingPlans } = useQuery<Plan[]>({
    queryKey: ["/api/admin/plans", { app: 'xevoicepro' }],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/plans?app=xevoicepro');
      if (!res.ok) throw new Error('Failed to fetch plans');
      return res.json();
    },
  });

  // Fetch credit packages (topups)
  const { data: topups, isLoading: isLoadingTopups } = useQuery<CreditPackage[]>({
    queryKey: ["/api/admin/credit-packages"],
  });

  // Fetch admin settings
  const { data: adminSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  // Fetch payment gateway configuration
  const { data: paymentGateway } = useQuery<PaymentGatewayConfig>({
    queryKey: ["/api/settings/payment-gateway"],
  });

  // Currency helpers
  const stripeCurrency = adminSettings?.stripe_currency || "USD";

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: "$", EUR: "€", GBP: "£", INR: "₹", AUD: "A$", CAD: "C$",
      JPY: "¥", CNY: "¥", NGN: "₦", GHS: "₵", ZAR: "R", KES: "KSh",
      BRL: "R$", MXN: "MX$", ARS: "AR$", CLP: "CLP$", COP: "COP$",
    };
    return symbols[currency] || currency + " ";
  };

  const stripeCurrencySymbol = getCurrencySymbol(stripeCurrency);

  // Filter plans by country
  const filteredPlans = (plans || [])
    .filter((plan) => plan.country === selectedCountry && !plan.name.startsWith('xehirepro_'))
    .sort((a, b) => {
      const isIndia = selectedCountry === "IN";
      const priceA = parseFloat(isIndia ? (a.razorpayMonthlyPrice ?? a.monthlyPrice ?? '0') : (a.monthlyPrice ?? '0'));
      const priceB = parseFloat(isIndia ? (b.razorpayMonthlyPrice ?? b.monthlyPrice ?? '0') : (b.monthlyPrice ?? '0'));
      return priceA - priceB;
    });

  // Filter topups by country
  const filteredTopups = (topups || []).filter(pkg => {
    // If country field exists, filter by it; otherwise show all
    if (pkg.country) {
      return pkg.country === selectedCountry;
    }
    return true;
  });

  // ==================== PLAN MUTATIONS ====================

  const createPlan = useMutation({
    mutationFn: async (data: PlanFormData) => {
      return apiRequest("POST", "/api/admin/plans", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Created",
        description: "The subscription plan has been created and synced to Stripe",
      });
      setIsPlanCreateDialogOpen(false);
      resetPlanForm();
    },
    onError: (error: any) => {
      toast({ title: "Failed to Create Plan", description: error.message, variant: "destructive" });
    },
  });

  const updatePlan = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PlanFormData> }) => {
      return apiRequest("PATCH", `/api/admin/plans/${id}`, { ...data, forceStripeSync: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Updated",
        description: "The subscription plan has been updated and synced to Stripe",
      });
      setIsPlanEditDialogOpen(false);
      setEditingPlan(null);
      resetPlanForm();
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update Plan", description: error.message, variant: "destructive" });
    },
  });

  const deletePlan = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/plans/${id}?deleteFromStripe=true`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Deleted",
        description: "The subscription plan has been deleted from database and Stripe",
      });
      setIsPlanDeleteDialogOpen(false);
      setDeletingPlan(null);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Delete Plan", description: error.message, variant: "destructive" });
    },
  });

  const syncPlanToStripe = useMutation({
    mutationFn: async (planId: string) => {
      return apiRequest("POST", `/api/admin/plans/${planId}/sync/stripe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({ title: "Plan Synced", description: "Plan synced to Stripe successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Sync Failed", description: error.message, variant: "destructive" });
    },
  });

  // ==================== TOPUP MUTATIONS ====================

  const createTopup = useMutation({
    mutationFn: async (data: TopupFormData) => {
      return apiRequest("POST", "/api/admin/credit-packages", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Topup Package Created",
        description: "The topup package has been created and synced to Stripe",
      });
      setIsTopupCreateDialogOpen(false);
      resetTopupForm();
    },
    onError: (error: any) => {
      toast({ title: "Failed to Create Topup", description: error.message, variant: "destructive" });
    },
  });

  const updateTopup = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TopupFormData> }) => {
      return apiRequest("PATCH", `/api/admin/credit-packages/${id}`, { ...data, forceStripeSync: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Topup Package Updated",
        description: "The topup package has been updated and synced to Stripe",
      });
      setIsTopupEditDialogOpen(false);
      setEditingTopup(null);
      resetTopupForm();
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update Topup", description: error.message, variant: "destructive" });
    },
  });

  const deleteTopup = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/credit-packages/${id}?deleteFromStripe=true`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({
        title: "Topup Package Deleted",
        description: "The topup package has been deleted from database and Stripe",
      });
      setIsTopupDeleteDialogOpen(false);
      setDeletingTopup(null);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Delete Topup", description: error.message, variant: "destructive" });
    },
  });

  const toggleTopupActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/admin/credit-packages/${id}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credit-packages"] });
      toast({ title: "Status Updated" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update Status", description: error.message, variant: "destructive" });
    },
  });

  // ==================== FORM HANDLERS ====================

  const resetPlanForm = () => setPlanFormData(defaultPlanFormData);
  const resetTopupForm = () => setTopupFormData(defaultTopupFormData);

  const handleEditPlan = (plan: Plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description,
      country: plan.country || "US",
      seatingEnabled: plan.seatingEnabled ?? false,
      maxSeats: plan.maxSeats || 1,
      monthlyPrice: parseFloat(plan.monthlyPrice),
      yearlyPrice: plan.yearlyPrice ? parseFloat(plan.yearlyPrice) : null,
      razorpayMonthlyPrice: plan.razorpayMonthlyPrice ? parseFloat(plan.razorpayMonthlyPrice) : null,
      razorpayYearlyPrice: plan.razorpayYearlyPrice ? parseFloat(plan.razorpayYearlyPrice) : null,
      maxAgents: plan.maxAgents,
      maxCampaigns: plan.maxCampaigns,
      maxContactsPerCampaign: plan.maxContactsPerCampaign,
      maxWebhooks: plan.maxWebhooks,
      maxKnowledgeBases: plan.maxKnowledgeBases,
      maxFlows: plan.maxFlows,
      maxPhoneNumbers: plan.maxPhoneNumbers,
      phoneNumberPurchaseCredits: plan.phoneNumberPurchaseCredits || 25,
      includedCredits: plan.includedCredits,
      perMinuteRate: plan.perMinuteRate ? parseFloat(plan.perMinuteRate) : null,
      canChooseLlm: plan.canChooseLlm,
      canPurchaseNumbers: plan.canPurchaseNumbers,
      useSystemPool: plan.useSystemPool,
    });
    setIsPlanEditDialogOpen(true);
  };

  const handleEditTopup = (pkg: CreditPackage) => {
    setEditingTopup(pkg);
    setTopupFormData({
      name: pkg.name,
      description: pkg.description || "",
      credits: pkg.credits,
      price: pkg.price,
      razorpayPrice: pkg.razorpayPrice || 0,
      paypalPrice: pkg.paypalPrice || 0,
      paystackPrice: pkg.paystackPrice || 0,
      mercadopagoPrice: pkg.mercadopagoPrice || 0,
      country: pkg.country || "US",
    });
    setIsTopupEditDialogOpen(true);
  };

  const handlePlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isFreeOrTrialPlan = /^(free|trial|free[-_]trial)$/i.test(planFormData.name.toLowerCase());
    const isIndia = planFormData.country === 'IN';

    if (isIndia) {
      if (!isFreeOrTrialPlan && (!planFormData.razorpayMonthlyPrice || planFormData.razorpayMonthlyPrice <= 0)) {
        toast({ title: 'Validation Error', description: 'Monthly price in INR is required for India plans', variant: 'destructive' });
        return;
      }
    } else {
      if (!isFreeOrTrialPlan && (!planFormData.monthlyPrice || planFormData.monthlyPrice <= 0)) {
        toast({ title: 'Validation Error', description: 'Monthly price in USD is required for US plans', variant: 'destructive' });
        return;
      }
    }

    if (editingPlan) {
      await updatePlan.mutateAsync({ id: editingPlan.id, data: planFormData });
    } else {
      await createPlan.mutateAsync(planFormData);
    }
  };

  const handleTopupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTopup) {
      await updateTopup.mutateAsync({ id: editingTopup.id, data: topupFormData });
    } else {
      await createTopup.mutateAsync(topupFormData);
    }
  };

  const isLoading = isLoadingPlans || isLoadingTopups;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Plan & Topup Management
              </CardTitle>
              <CardDescription>
                Manage subscription plans and credit topup packages for your platform
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="IN">India</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="plans" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Subscription Plans
                <Badge variant="secondary" className="ml-1">{filteredPlans.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="topups" className="flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Topup Packages
                <Badge variant="secondary" className="ml-1">{filteredTopups.length}</Badge>
              </TabsTrigger>
            </TabsList>

            {/* PLANS TAB */}
            <TabsContent value="plans">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setIsPlanCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Plan
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPlans.map((plan) => (
                  <Card key={plan.id} className={`relative ${!plan.isActive ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{plan.displayName}</CardTitle>
                          <CardDescription className="mt-1">{plan.description}</CardDescription>
                        </div>
                        <div className="flex gap-1">
                          {plan.stripeProductId ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <Check className="h-3 w-3 mr-1" />
                              Stripe
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              <X className="h-3 w-3 mr-1" />
                              Not Synced
                            </Badge>
                          )}
                        </div>
                      </div>
                      {(() => {
                        const isIndia = selectedCountry === "IN";
                        const monthlyAmount = isIndia ? (plan.razorpayMonthlyPrice ?? plan.monthlyPrice) : plan.monthlyPrice;
                        const yearlyAmount = isIndia ? (plan.razorpayYearlyPrice ?? plan.yearlyPrice) : plan.yearlyPrice;
                        const currencySymbol = isIndia ? "₹" : "$";
                        return (
                          <div className="mt-4">
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl font-bold">
                                {monthlyAmount !== null && monthlyAmount !== undefined
                                  ? `${currencySymbol}${parseFloat(monthlyAmount).toFixed(2)}`
                                  : "—"}
                              </span>
                              <span className="text-muted-foreground">/month</span>
                            </div>
                            {yearlyAmount !== null && yearlyAmount !== undefined && parseFloat(yearlyAmount) > 0 && (
                              <div className="text-sm text-muted-foreground mt-1">
                                {currencySymbol}{parseFloat(yearlyAmount).toFixed(2)}/year
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span>{plan.includedCredits.toLocaleString()} credits (mins)</span>
                      </div>
                      {plan.perMinuteRate && (
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span>{selectedCountry === "IN" ? "₹" : "$"}{parseFloat(plan.perMinuteRate).toFixed(2)}/min</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{plan.maxAgents} agents</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{plan.maxPhoneNumbers} phone numbers</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span>{plan.maxCampaigns} campaigns</span>
                      </div>

                      <div className="flex flex-wrap gap-1 pt-2">
                        {plan.canPurchaseNumbers && (
                          <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">Can Purchase Numbers</span>
                        )}
                        {plan.useSystemPool && (
                          <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">System Pool</span>
                        )}
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button variant="outline" size="sm" onClick={() => handleEditPlan(plan)} className="flex-1">
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        {!plan.stripeProductId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => syncPlanToStripe.mutate(plan.id)}
                            disabled={syncPlanToStripe.isPending}
                          >
                            <RefreshCw className={`h-3 w-3 mr-1 ${syncPlanToStripe.isPending ? 'animate-spin' : ''}`} />
                            Sync
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setDeletingPlan(plan); setIsPlanDeleteDialogOpen(true); }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredPlans.length === 0 && (
                <Alert>
                  <AlertDescription className="text-center py-8">
                    No subscription plans found for {selectedCountry === 'US' ? 'United States' : 'India'}. Create your first plan to get started.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* TOPUPS TAB */}
            <TabsContent value="topups">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setIsTopupCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Topup Package
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTopups.map((pkg) => (
                  <Card key={pkg.id} className={!pkg.isActive ? "opacity-60" : ""}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Zap className="h-4 w-4 text-yellow-500" />
                            {pkg.name}
                          </CardTitle>
                          {pkg.description && (
                            <CardDescription className="mt-1">{pkg.description}</CardDescription>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {pkg.stripeProductId ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <Check className="h-3 w-3 mr-1" />
                              Stripe
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              <X className="h-3 w-3 mr-1" />
                              Not Synced
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold">{pkg.credits} credits</span>
                        </div>
                        <div className="flex justify-between items-center text-lg">
                          <span className="text-muted-foreground">{stripeCurrency} (Stripe):</span>
                          <span className="font-semibold">{stripeCurrencySymbol}{pkg.price}</span>
                        </div>
                        {paymentGateway?.razorpayEnabled && (
                          <div className="flex justify-between items-center text-lg">
                            <span className="text-muted-foreground">INR (Razorpay):</span>
                            <span className={pkg.razorpayPrice ? "font-semibold" : "text-sm text-orange-500"}>
                              {pkg.razorpayPrice ? `₹${pkg.razorpayPrice}` : "Not configured"}
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-muted-foreground border-t pt-2 mt-2">
                          {stripeCurrencySymbol}{(pkg.price / pkg.credits).toFixed(3)} per credit
                        </div>

                        <div className="flex gap-2 pt-4">
                          <Button variant="outline" size="sm" onClick={() => handleEditTopup(pkg)} className="flex-1">
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant={pkg.isActive ? "outline" : "default"}
                            size="sm"
                            onClick={() => toggleTopupActive.mutate({ id: pkg.id, isActive: !pkg.isActive })}
                          >
                            {pkg.isActive ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setDeletingTopup(pkg); setIsTopupDeleteDialogOpen(true); }}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredTopups.length === 0 && (
                <Alert>
                  <AlertDescription className="text-center py-8">
                    No topup packages found. Create your first topup package to get started.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* PLAN CREATE/EDIT DIALOG */}
      <Dialog open={isPlanCreateDialogOpen || isPlanEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsPlanCreateDialogOpen(false);
          setIsPlanEditDialogOpen(false);
          setEditingPlan(null);
          resetPlanForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
            <DialogDescription>
              {editingPlan ? 'Update the subscription plan details. Changes will be synced to Stripe.' : 'Configure a new subscription plan. It will be automatically synced to Stripe.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handlePlanSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plan Name (Internal)</Label>
                  <Input
                    value={planFormData.name}
                    onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
                    placeholder="e.g., starter, growth, scale"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">Lowercase, no spaces (used in code)</p>
                </div>
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={planFormData.displayName}
                    onChange={(e) => setPlanFormData({ ...planFormData, displayName: e.target.value })}
                    placeholder="e.g., Starter Plan"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={planFormData.description}
                  onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
                  placeholder="Brief description of the plan"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Country</Label>
                  <Select value={planFormData.country} onValueChange={(value) => setPlanFormData({ ...planFormData, country: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="IN">India</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Seat-Based Plan</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={planFormData.seatingEnabled}
                      onChange={(e) => setPlanFormData({ ...planFormData, seatingEnabled: e.target.checked })}
                    />
                    <span className="text-sm">Enable multi-seat usage</span>
                  </div>
                  {planFormData.seatingEnabled && (
                    <>
                      <Label className="mt-2 block">Number of Seats</Label>
                      <Input
                        type="number"
                        min="1"
                        value={planFormData.maxSeats}
                        onChange={(e) => setPlanFormData({ ...planFormData, maxSeats: parseInt(e.target.value, 10) || 1 })}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Pricing</h4>
                <div className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">
                  {planFormData.country === 'IN' ? '₹ INR Plan' : '$ USD Plan'}
                </div>
              </div>

              {planFormData.country !== 'IN' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Monthly Price ($) <span className="text-red-500">*</span></Label>
                      <InfoTooltip content="Monthly subscription price in USD" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planFormData.monthlyPrice}
                      onChange={(e) => setPlanFormData({ ...planFormData, monthlyPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Yearly Price ($)</Label>
                      <InfoTooltip content="Optional yearly subscription price" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planFormData.yearlyPrice || 0}
                      onChange={(e) => setPlanFormData({ ...planFormData, yearlyPrice: parseFloat(e.target.value) || null })}
                    />
                  </div>
                </div>
              )}

              {planFormData.country === 'IN' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Monthly Price (₹ INR) <span className="text-red-500">*</span></Label>
                      <InfoTooltip content="Monthly subscription price in Indian Rupees" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planFormData.razorpayMonthlyPrice || 0}
                      onChange={(e) => setPlanFormData({ ...planFormData, razorpayMonthlyPrice: parseFloat(e.target.value) || null })}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Yearly Price (₹ INR)</Label>
                      <InfoTooltip content="Optional yearly subscription price" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planFormData.razorpayYearlyPrice || 0}
                      onChange={(e) => setPlanFormData({ ...planFormData, razorpayYearlyPrice: parseFloat(e.target.value) || null })}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Per Minute Rate ({planFormData.country === 'IN' ? '₹' : '$'})</Label>
                    <InfoTooltip content="Cost per minute for credit calculation" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planFormData.perMinuteRate || ''}
                    onChange={(e) => setPlanFormData({ ...planFormData, perMinuteRate: parseFloat(e.target.value) || null })}
                    placeholder="e.g., 5.50"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Included Credits (Minutes)</Label>
                    <InfoTooltip content="Number of voice call minutes included per month" />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={planFormData.includedCredits}
                    onChange={(e) => setPlanFormData({ ...planFormData, includedCredits: parseInt(e.target.value, 10) || 0 })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Limits */}
            <div className="space-y-4">
              <h4 className="font-semibold">Resource Limits</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Agents</Label>
                  <Input type="number" min="1" value={planFormData.maxAgents} onChange={(e) => setPlanFormData({ ...planFormData, maxAgents: parseInt(e.target.value, 10) || 1 })} required />
                </div>
                <div>
                  <Label>Max Phone Numbers</Label>
                  <Input type="number" min="0" value={planFormData.maxPhoneNumbers} onChange={(e) => setPlanFormData({ ...planFormData, maxPhoneNumbers: parseInt(e.target.value, 10) || 0 })} required />
                </div>
                <div>
                  <Label>Max Campaigns</Label>
                  <Input type="number" min="1" value={planFormData.maxCampaigns} onChange={(e) => setPlanFormData({ ...planFormData, maxCampaigns: parseInt(e.target.value, 10) || 1 })} required />
                </div>
                <div>
                  <Label>Max Contacts Per Campaign</Label>
                  <Input type="number" min="1" value={planFormData.maxContactsPerCampaign} onChange={(e) => setPlanFormData({ ...planFormData, maxContactsPerCampaign: parseInt(e.target.value, 10) || 100 })} required />
                </div>
                <div>
                  <Label>Max Webhooks</Label>
                  <Input type="number" min="0" value={planFormData.maxWebhooks} onChange={(e) => setPlanFormData({ ...planFormData, maxWebhooks: parseInt(e.target.value, 10) || 3 })} required />
                </div>
                <div>
                  <Label>Max Knowledge Bases</Label>
                  <Input type="number" min="0" value={planFormData.maxKnowledgeBases} onChange={(e) => setPlanFormData({ ...planFormData, maxKnowledgeBases: parseInt(e.target.value, 10) || 5 })} required />
                </div>
                <div>
                  <Label>Max Flows</Label>
                  <Input type="number" min="0" value={planFormData.maxFlows} onChange={(e) => setPlanFormData({ ...planFormData, maxFlows: parseInt(e.target.value, 10) || 3 })} required />
                </div>
                <div>
                  <Label>Phone Purchase Credits</Label>
                  <Input type="number" min="0" value={planFormData.phoneNumberPurchaseCredits} onChange={(e) => setPlanFormData({ ...planFormData, phoneNumberPurchaseCredits: parseInt(e.target.value, 10) || 25 })} required />
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="space-y-4">
              <h4 className="font-semibold">Plan Permissions</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 p-3 rounded border">
                  <input type="checkbox" id="canPurchaseNumbers" checked={planFormData.canPurchaseNumbers} onChange={(e) => setPlanFormData({ ...planFormData, canPurchaseNumbers: e.target.checked })} />
                  <div>
                    <Label htmlFor="canPurchaseNumbers" className="cursor-pointer">Can Purchase Phone Numbers</Label>
                    <p className="text-xs text-muted-foreground">Allow users to buy/rent phone numbers</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 rounded border">
                  <input type="checkbox" id="useSystemPool" checked={planFormData.useSystemPool} onChange={(e) => setPlanFormData({ ...planFormData, useSystemPool: e.target.checked })} />
                  <div>
                    <Label htmlFor="useSystemPool" className="cursor-pointer">Use System Phone Pool</Label>
                    <p className="text-xs text-muted-foreground">Use shared system numbers instead of dedicated</p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsPlanCreateDialogOpen(false); setIsPlanEditDialogOpen(false); setEditingPlan(null); resetPlanForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={createPlan.isPending || updatePlan.isPending}>
                {(createPlan.isPending || updatePlan.isPending) ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{editingPlan ? 'Updating...' : 'Creating...'}</>
                ) : (
                  editingPlan ? 'Update Plan' : 'Create Plan'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PLAN DELETE DIALOG */}
      <Dialog open={isPlanDeleteDialogOpen} onOpenChange={setIsPlanDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the &quot;{deletingPlan?.displayName}&quot; plan? This will also archive the product in Stripe. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPlanDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deletingPlan && deletePlan.mutate(deletingPlan.id)} disabled={deletePlan.isPending}>
              {deletePlan.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOPUP CREATE/EDIT DIALOG */}
      <Dialog open={isTopupCreateDialogOpen || isTopupEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsTopupCreateDialogOpen(false);
          setIsTopupEditDialogOpen(false);
          setEditingTopup(null);
          resetTopupForm();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTopup ? 'Edit Topup Package' : 'Create Topup Package'}</DialogTitle>
            <DialogDescription>
              {editingTopup ? 'Update the topup package details. Changes will be synced to Stripe.' : 'Configure a new topup package. It will be automatically synced to Stripe.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTopupSubmit} className="space-y-4">
            <div>
              <Label>Package Name</Label>
              <Input
                value={topupFormData.name}
                onChange={(e) => setTopupFormData({ ...topupFormData, name: e.target.value })}
                placeholder="e.g., 500 Credits Pack"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={topupFormData.description}
                onChange={(e) => setTopupFormData({ ...topupFormData, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Select value={topupFormData.country} onValueChange={(value) => setTopupFormData({ ...topupFormData, country: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="IN">India</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credits</Label>
              <Input
                type="number"
                value={topupFormData.credits}
                onChange={(e) => setTopupFormData({ ...topupFormData, credits: parseInt(e.target.value) || 0 })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Label>Price ({stripeCurrency} - Stripe)</Label>
                  <InfoTooltip content={`Price for Stripe payments in ${stripeCurrency}`} />
                </div>
                <Input
                  type="number"
                  step="0.01"
                  value={topupFormData.price}
                  onChange={(e) => setTopupFormData({ ...topupFormData, price: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              {paymentGateway?.razorpayEnabled && (
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Price (INR - Razorpay)</Label>
                    <InfoTooltip content="Price for Razorpay payments in INR" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={topupFormData.razorpayPrice}
                    onChange={(e) => setTopupFormData({ ...topupFormData, razorpayPrice: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>
            {topupFormData.credits > 0 && (
              <div className="text-sm text-muted-foreground">
                Price per credit: {stripeCurrencySymbol}{(topupFormData.price / topupFormData.credits).toFixed(3)}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsTopupCreateDialogOpen(false); setIsTopupEditDialogOpen(false); setEditingTopup(null); resetTopupForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={createTopup.isPending || updateTopup.isPending}>
                {(createTopup.isPending || updateTopup.isPending) ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{editingTopup ? 'Updating...' : 'Creating...'}</>
                ) : (
                  editingTopup ? 'Update Package' : 'Create Package'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* TOPUP DELETE DIALOG */}
      <Dialog open={isTopupDeleteDialogOpen} onOpenChange={setIsTopupDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Topup Package</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the &quot;{deletingTopup?.name}&quot; topup package? This will also archive the product in Stripe. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTopupDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deletingTopup && deleteTopup.mutate(deletingTopup.id)} disabled={deleteTopup.isPending}>
              {deleteTopup.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
